import { ContactsInput } from "@/components/contacts";
import { DomainUpsert } from "@/components/domain";
import { SelectSearch } from "@/components/select-search";
import { TagsInput } from "@/components/tags-input";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  AgencyTourTypesAsArray,
  BriefingLanguages,
  Countries,
  CreateAgency,
  DigitalBookTypesAsArray,
  GuidingTypesAsArray,
  Languages,
  NavigationApp,
  PaymentCompany,
  PaymentCompanyTypesAsArray,
  PaymentConditions,
  PaymentConditionsTypesAsArray,
} from "@/models";
import { FormOptions } from "@deadcow-enterprises/react-forms";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export function Form({
  model,
  onUpsert,
}: {
  model?: CreateAgency;
  onUpsert: (data: CreateAgency) => Promise<void>;
}) {
  const { t } = useTranslation();
  const isEdit = useMemo(() => !!model, [model]);
  const formConfig = useMemo<FormOptions<CreateAgency>>(() => {
    const config: FormOptions<CreateAgency> = {
      year: {
        value: model?.year ?? new Date().getFullYear(),
      },
      name: {
        value: model?.name ?? "",
        validator: z.string().min(1, "errors.required"),
      },
      country: {
        value: model?.country,
      },
      contacts: {
        value: model?.contacts ?? [],
      },
      website: {
        value: model?.website,
      },
      notes: {
        value: model?.notes,
      },
      tags: {
        value: model?.tags,
      },
      language: {
        value: model?.language,
      },
      priceCommission: {
        value: model?.priceCommission,
      },
      seasonSupplementCommission: {
        value: model?.seasonSupplementCommission,
      },
      individualRoomSupplementCommission: {
        value: model?.individualRoomSupplementCommission,
      },
      soloTravelerCommission: {
        value: model?.soloTravelerCommission,
      },
      bikesCommissions: {
        value: model?.bikesCommissions,
      },
      extraNightsCommissions: {
        value: model?.extraNightsCommissions,
      },
      roomChangeCommissions: {
        value: model?.roomChangeCommissions,
      },
      servicesCommissions: {
        value: model?.servicesCommissions,
      },
      paymentConditions: {
        value: model?.paymentConditions,
      },
      timeRestraintForDocumentation: {
        value: model?.timeRestraintForDocumentation,
      },
      branding: {
        value: model?.branding ?? false,
      },
      payment: {
        value: model?.payment,
      },
      favorite: {
        value: model?.favorite ?? false,
      },
      details: {
        value: model?.details,
      },
      digitalBook: {
        value: model?.digitalBook || undefined,
      },
      navigationApp: {
        value: model?.navigationApp || undefined,
      },
      tours: {
        value: model?.tours,
      },
      guiding: {
        value: model?.guiding,
      },
      briefingLanguage: {
        value: model?.briefingLanguage,
      },
      travefyRequired: {
        value: model?.travefyRequired,
      },
      rwgpsrequired: {
        value: model?.rwgpsrequired,
      },
    };
    return config;
  }, [model]);

  return (
    <DomainUpsert
      title={model?.name ?? t("agencies.new.title")}
      buttonTranslation={
        isEdit ? "agencies.update.button" : "agencies.new.button"
      }
      formConfig={formConfig}
      onUpsert={onUpsert}
      fields={[
        {
          key: "name",
          label: "agencies.fields.name",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.name}
              onChange={(ev) => {
                form.setValue("name", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "contacts",
          label: "agencies.fields.contacts",
          input: (form, id) => (
            <ContactsInput
              id={id}
              contacts={form.value.contacts ?? []}
              onChange={(v) => {
                form.setValue("contacts", v);
              }}
            />
          ),
        },
        {
          key: "website",
          label: "agencies.fields.website",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.website ?? ""}
              onChange={(ev) => {
                form.setValue("website", ev.target.value);
              }}
            />
          ),
        },
        {
          key: "notes",
          label: "agencies.fields.notes",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.notes ?? ""}
              onChange={(e) => {
                form.setValue("notes", e.target.value);
              }}
            />
          ),
        },
        {
          key: "country",
          label: "agencies.fields.country",
          input: (form, id) => (
            <SelectSearch
              id={id}
              value={form.value.country ?? undefined}
              onChange={(v) => {
                form.setValue("country", v);
              }}
              options={
                Countries.map((o) => ({
                  value: o,
                  label: t(`countries.${o}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "tags",
          label: "agencies.fields.tags",
          input: (form, id) => (
            <TagsInput
              id={id}
              tags={form.value.tags ?? []}
              setTags={(tags) => {
                form.setValue("tags", tags);
              }}
            />
          ),
        },
        {
          key: "language",
          label: "agencies.fields.language",
          input: (form, id) => (
            <SelectSearch
              id={id}
              options={Languages.map((c) => ({
                value: c,
                label: c,
              }))}
              value={form.value.language}
              onChange={(v) => {
                form.setValue("language", v);
              }}
            />
          ),
        },
        {
          key: "priceCommission",
          label: "agencies.fields.priceCommission",
          input: (form, id) => (
            <Input
              id={id}
              type="number"
              step={0.01}
              min={0}
              value={form.value.priceCommission ?? ""}
              onChange={(ev) => {
                form.setValue(
                  "priceCommission",
                  Number.parseFloat(ev.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "seasonSupplementCommission",
          label: "agencies.fields.seasonSupplementCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.seasonSupplementCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "seasonSupplementCommission",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "individualRoomSupplementCommission",
          label: "agencies.fields.individualRoomSupplementCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.individualRoomSupplementCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "individualRoomSupplementCommission",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "soloTravelerCommission",
          label: "agencies.fields.soloTravelerCommission",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.soloTravelerCommission ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "soloTravelerCommission",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "bikesCommissions",
          label: "agencies.fields.bikesCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.bikesCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "bikesCommissions",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "extraNightsCommissions",
          label: "agencies.fields.extraNightsCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.extraNightsCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "extraNightsCommissions",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "roomChangeCommissions",
          label: "agencies.fields.roomChangeCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.roomChangeCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "roomChangeCommissions",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "servicesCommissions",
          label: "agencies.fields.servicesCommissions",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.servicesCommissions ?? ""}
              type="number"
              step={0.01}
              min={0}
              onChange={(e) => {
                form.setValue(
                  "servicesCommissions",
                  Number.parseFloat(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "paymentConditions",
          label: "agencies.fields.paymentConditions",
          input: (form, id) => (
            <SelectSearch
              id={id}
              options={PaymentConditionsTypesAsArray.map((c) => ({
                value: c,
                label: t(`agencies.paymentConditions.${c}`),
              }))}
              value={form.value.paymentConditions}
              onChange={(v) => {
                form.setValue("paymentConditions", v as PaymentConditions);
              }}
            />
          ),
        },
        {
          key: "timeRestraintForDocumentation",
          label: "agencies.fields.timeRestraintForDocumentation",
          input: (form, id) => (
            <Input
              id={id}
              value={form.value.timeRestraintForDocumentation ?? ""}
              type="number"
              min={0}
              onChange={(e) => {
                form.setValue(
                  "timeRestraintForDocumentation",
                  parseInt(e.target.value)
                );
              }}
            />
          ),
        },
        {
          key: "branding",
          label: "agencies.fields.branding",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("branding", v);
              }}
              checked={!!form.value.branding}
            />
          ),
        },
        {
          key: "payment",
          label: "agencies.fields.payment",
          input: (form, id) => (
            <SelectSearch
              id={id}
              options={PaymentCompanyTypesAsArray.map((c) => ({
                value: c,
                label: t(`agencies.payment.${c}`),
              }))}
              value={form.value.payment}
              onChange={(v) => {
                form.setValue("payment", v as PaymentCompany);
              }}
            />
          ),
        },
        {
          key: "details",
          label: "agencies.fields.details",
          input: (form, id) => (
            <Textarea
              id={id}
              value={form.value.details ?? ""}
              onChange={(e) => {
                form.setValue("details", e.target.value);
              }}
            />
          ),
        },
        {
          key: "digitalBook",
          label: "agencies.fields.digitalBook",
          input: (form, id) => (
            <SelectSearch
              id={id}
              options={DigitalBookTypesAsArray.map((c) => ({
                value: c,
                label: t(`agencies.digitalBook.${c}`),
              }))}
              value={form.value.digitalBook}
              onChange={(v) => {
                form.setValue("digitalBook", v);
              }}
            />
          ),
        },
        {
          key: "navigationApp",
          label: "agencies.fields.navigationApp",
          input: (form, id) => (
            <SelectSearch
              id={id}
              options={NavigationApp.map((c) => ({
                value: c,
                label: t(`agencies.navigationApp.${c}`),
              }))}
              value={form.value.navigationApp}
              onChange={(v) => {
                form.setValue("navigationApp", v);
              }}
            />
          ),
        },
        {
          key: "tours",
          label: "agencies.fields.tours",
          input: (form, id) => (
            <MultiSelect
              id={id}
              selected={form.value.tours ?? []}
              className="w-full"
              onChange={(v) => {
                form.setValue("tours", v);
              }}
              options={
                AgencyTourTypesAsArray?.map((v) => ({
                  value: v,
                  label: t(`agencies.toursEnum.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "guiding",
          label: "agencies.fields.guiding",
          input: (form, id) => (
            <MultiSelect
              id={id}
              selected={form.value.guiding ?? []}
              className="w-full"
              onChange={(v) => {
                form.setValue("guiding", v);
              }}
              options={
                GuidingTypesAsArray?.map((v) => ({
                  value: v,
                  label: t(`agencies.guiding.${v}`),
                })) ?? []
              }
            />
          ),
        },
        {
          key: "briefingLanguage",
          label: "agencies.fields.briefingLanguage",
          input: (form, id) => (
            <MultiSelect
              id={id}
              selected={form.value.briefingLanguage ?? []}
              className="w-full"
              onChange={(v) => {
                form.setValue("briefingLanguage", v);
              }}
              options={
                BriefingLanguages?.map((v) => ({
                  value: v,
                  label: v,
                })) ?? []
              }
            />
          ),
        },
        {
          key: "travefyRequired",
          label: "agencies.fields.travefyRequired",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("travefyRequired", v);
              }}
              checked={!!form.value.travefyRequired}
            />
          ),
        },
        {
          key: "rwgpsrequired",
          label: "agencies.fields.rwgpsrequired",
          orientation: "horizontal",
          input: (form, id) => (
            <Switch
              id={id}
              className="ml-auto"
              onCheckedChange={(v) => {
                form.setValue("rwgpsrequired", v);
              }}
              checked={!!form.value.rwgpsrequired}
            />
          ),
        },
      ]}
    />
  );
}
